import React from "react";
import { Box, Typography, Avatar, Stack, Chip } from "@mui/material";
import { formatTotalScore } from "../../../utils/finalNightScoring";

const gold = "#C9A66B";

function sortLeaderboard(rows = []) {
  return [...rows].sort((a, b) => {
    const aElim = Boolean(a.contestant?.is_eliminated);
    const bElim = Boolean(b.contestant?.is_eliminated);
    if (aElim !== bElim) return aElim ? 1 : -1;
    return Number(b.total_score) - Number(a.total_score);
  });
}

export default function AdminRoundLeaderboard({
  rows = [],
  emptyMessage = "No scores yet for this round.",
  compact = false,
}) {
  const sorted = sortLeaderboard(rows);
  const activeRankByRowId = new Map();
  sorted
    .filter((r) => !r.contestant?.is_eliminated)
    .forEach((r, i) => activeRankByRowId.set(r.id, i + 1));

  if (sorted.length === 0) {
    return (
      <Box
        sx={{
          py: compact ? 2 : 3,
          px: 2,
          textAlign: "center",
          color: "text.secondary",
          fontSize: 13,
          borderRadius: 2,
          bgcolor: "#fafbfc",
          border: "1px dashed #e5e7eb",
        }}
      >
        {emptyMessage}
      </Box>
    );
  }

  return (
    <Stack spacing={1}>
      {sorted.map((row) => {
        const c = row.contestant;
        if (!c) return null;
        const eliminated = Boolean(c.is_eliminated);
        const activeRank = activeRankByRowId.get(row.id) || null;
        const judgeAvg =
          row.judge_score_count > 0
            ? Math.round(row.judge_score_sum / row.judge_score_count)
            : "—";

        return (
          <Box
            key={row.id}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              p: compact ? 1.2 : 1.5,
              borderRadius: 2,
              bgcolor: eliminated
                ? "#f3f4f6"
                : activeRank && activeRank <= 3
                  ? "#fffbf5"
                  : "#fafbfc",
              border: "1px solid #eceef3",
              opacity: eliminated ? 0.72 : 1,
            }}
          >
            <Typography
              fontWeight={800}
              sx={{ width: 28, color: eliminated ? "#9ca3af" : gold }}
            >
              {eliminated ? "—" : `#${activeRank}`}
            </Typography>
            <Avatar
              src={c.profile_image}
              sx={{ width: compact ? 36 : 40, height: compact ? 36 : 40 }}
            />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
                <Typography
                  fontWeight={600}
                  noWrap
                  sx={{
                    color: eliminated ? "#6b7280" : "inherit",
                    textDecoration: eliminated ? "line-through" : "none",
                  }}
                >
                  {c.full_name}
                </Typography>
                {eliminated ? (
                  <Chip size="small" label="Eliminated" sx={{ height: 22, fontSize: 11 }} />
                ) : null}
              </Stack>
              <Typography fontSize={12} color="text.secondary">
                {row.public_votes} votes · Judge {judgeAvg}/10
                {row.admin_adjustment ? ` · Adj ${row.admin_adjustment}` : ""}
              </Typography>
            </Box>
            <Stack alignItems="flex-end" spacing={0.25}>
              <Typography fontWeight={800} sx={{ color: eliminated ? "#9ca3af" : gold }}>
                {formatTotalScore(row.total_score)}
              </Typography>
            </Stack>
          </Box>
        );
      })}
    </Stack>
  );
}
